import type { HTMLAttributes, ReactNode } from "react";
import Link from "next/link";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type PageHeaderProps = {
  actions?: ReactNode;
  eyebrow?: string;
  eyebrowHref?: string;
  meta?: ReactNode;
  title: string;
  description?: string;
};

export function PageHeader({
  actions,
  description,
  eyebrow,
  eyebrowHref,
  meta,
  title,
}: PageHeaderProps) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        {eyebrow ? (
          <p className="text-xs font-semibold uppercase tracking-normal text-[var(--color-brand)]">
            {eyebrowHref ? (
              <Link
                className="rounded-sm underline-offset-4 transition-colors hover:text-[var(--color-brand-strong)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
                href={eyebrowHref}
              >
                {eyebrow}
              </Link>
            ) : eyebrow}
          </p>
        ) : null}
        <h1 className="mt-1 text-2xl font-semibold leading-tight text-[var(--color-foreground)] sm:text-3xl">
          {title}
        </h1>
        {description ? (
          <p className="mt-2 max-w-3xl text-sm leading-6 text-[var(--color-foreground-muted)]">
            {description}
          </p>
        ) : null}
        {meta ? <div className="mt-3 flex flex-wrap gap-2">{meta}</div> : null}
      </div>
      {actions ? <div className="flex w-full min-w-0 max-w-full flex-wrap gap-2 sm:w-auto">{actions}</div> : null}
    </header>
  );
}

type StatCardProps = {
  ariaValue?: string;
  icon?: ReactNode;
  label: string;
  tone?: "danger" | "neutral" | "success" | "warning";
  value: ReactNode;
};

export type KpiMetric = StatCardProps;

type KpiRailProps = {
  compact?: boolean;
  className?: string;
  items: KpiMetric[];
};

export function KpiRail({ className, compact = false, items }: KpiRailProps) {
  if (compact) {
    return (
      <div
        className={cn("grid grid-cols-3 gap-2 sm:flex", className)}
        data-kpi-summary
        data-kpi-cards
      >
        {items.map((item) => <StatCard {...item} compact key={item.label} />)}
      </div>
    );
  }
  return (
    <div className={className} data-kpi-summary>
      <div
        className={cn(
          "grid gap-px overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-border)] sm:hidden",
          mobileKpiGridClass(items.length),
        )}
        data-kpi-rail
      >
        {items.map((item, index) => (
          <div
            aria-label={`${item.label}: ${item.ariaValue ?? (typeof item.value === "string" || typeof item.value === "number" ? item.value : "value shown")}`}
            className={cn(
              "min-w-0 bg-[var(--color-surface)] px-1 py-2.5 text-center",
              items.length === 5 && "col-span-2 min-[360px]:col-span-1 min-[360px]:col-start-auto",
              items.length === 5 && index === 3 && "col-start-2",
              items.length === 5 && index === 4 && "col-start-4",
            )}
            data-kpi-item
            key={item.label}
          >
            <p
              className={cn(
                "truncate text-xl font-semibold leading-none",
                item.tone === "danger" ? "text-[var(--color-danger)]" : null,
                item.tone === "warning" ? "text-[var(--color-warning)]" : null,
                item.tone === "success" ? "text-[var(--color-success)]" : null,
              )}
            >
              {item.value}
            </p>
            <p className="mt-1 truncate text-[11px] font-medium leading-none text-[var(--color-foreground-muted)]">
              {item.label}
            </p>
          </div>
        ))}
      </div>

      <div
        className={cn("hidden gap-3 sm:grid", desktopKpiGridClass(items.length))}
        data-kpi-cards
      >
        {items.map((item) => (
          <StatCard {...item} key={item.label} />
        ))}
      </div>
    </div>
  );
}

function mobileKpiGridClass(itemCount: number) {
  if (itemCount === 1) return "grid-cols-1";
  if (itemCount === 2) return "grid-cols-2";
  if (itemCount === 3) return "grid-cols-3";
  if (itemCount === 4) return "grid-cols-4";
  if (itemCount === 5) return "grid-cols-6 min-[360px]:grid-cols-5";
  return "grid-cols-3";
}

function desktopKpiGridClass(itemCount: number) {
  if (itemCount === 1) return "grid-cols-1";
  if (itemCount === 2) return "grid-cols-2";
  if (itemCount === 3) return "grid-cols-3";
  if (itemCount === 4) return "grid-cols-2 lg:grid-cols-4";
  if (itemCount === 5) return "grid-cols-2 xl:grid-cols-5";
  return "grid-cols-2 lg:grid-cols-3";
}

export function StatCard({ compact = false, icon, label, tone = "neutral", value }: StatCardProps & { compact?: boolean }) {
  return (
    <div className={cn("rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[0_1px_2px_var(--shadow-soft)]", compact ? "flex aspect-square min-w-0 flex-col items-center justify-center p-2 sm:w-0 sm:max-w-28 sm:flex-1" : "p-4")}>
      <div className={cn("flex items-center", compact ? "flex-col-reverse gap-1" : "justify-between gap-3")}>
        <p className={cn("font-semibold uppercase tracking-normal text-[var(--color-foreground-muted)]", compact ? "text-[10px]" : "text-xs")}>
          {label}
        </p>
        {icon ? (
          <span
            className={cn(
              "inline-flex items-center justify-center rounded-md",
              compact ? "size-6" : "size-8",
              tone === "danger"
                ? "bg-[color-mix(in_srgb,var(--color-danger-fill)_12%,white)] text-[var(--color-danger)]"
                : null,
              tone === "warning"
                ? "bg-[color-mix(in_srgb,var(--color-warning-fill)_16%,white)] text-[var(--color-warning)]"
                : null,
              tone === "success"
                ? "bg-[color-mix(in_srgb,var(--color-success)_10%,white)] text-[var(--color-success)]"
                : null,
              tone === "neutral"
                ? "bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]"
                : null,
            )}
          >
            {icon}
          </span>
        ) : null}
      </div>
      <p
        className={cn(
          "font-semibold leading-none",
          compact ? "mt-1 text-2xl tabular-nums" : "mt-3 text-3xl",
          tone === "danger" ? "text-[var(--color-danger)]" : null,
          tone === "warning" ? "text-[var(--color-warning)]" : null,
        )}
      >
        {value}
      </p>
    </div>
  );
}

export function Surface({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  return (
    <section
      className={cn(
        "rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[0_1px_2px_var(--shadow-soft)]",
        className,
      )}
      {...props}
    >
      {children}
    </section>
  );
}

export function MetaBadge({ children }: { children: ReactNode }) {
  return (
    <Badge className="rounded-md px-2.5 py-1" tone="neutral">
      {children}
    </Badge>
  );
}
